import { create } from 'zustand';
import { toast } from 'sonner';
import { i18n } from '@/i18n';
import type { GuidedTour, SpotlightPosition } from '../model/types';
import { findGuidedTour } from '../model/tours';
import { onboardingService } from '../service/onboarding.service';
import { syncOnboardingCache } from '../hooks/useOnboardingStateQuery';
import {
  trackTutorialCompleted,
  trackTutorialStarted,
  tutorialStepId,
} from '@/features/tutorials/model/progress';

/**
 * Where each tour left with "Reprendre plus tard" stopped (tour id → step index). Per browser and
 * per user (a shared club tablet must not hand one coach's tour to the next account): a
 * convenience, not progress.
 */
export type PausedTours = Readonly<Record<string, number>>;

const NO_PAUSED_TOURS: PausedTours = {};

export const PAUSED_TOUR_STORAGE_KEY = 'vsp.tutorials.pausedTour';

const storageKey = (userId: string) => `${PAUSED_TOUR_STORAGE_KEY}:${userId}`;

function readPausedTours(userId: string): PausedTours {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return NO_PAUSED_TOURS;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return NO_PAUSED_TOURS;
    // Single-slot shape written before one pause per tour was kept.
    if ('tourId' in parsed && 'stepIndex' in parsed) {
      return typeof parsed.tourId === 'string' && typeof parsed.stepIndex === 'number'
        ? { [parsed.tourId]: parsed.stepIndex }
        : NO_PAUSED_TOURS;
    }
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, number] => typeof entry[1] === 'number'),
    );
  } catch {
    // Storage blocked or corrupted: start over.
  }
  return NO_PAUSED_TOURS;
}

function writePausedTours(userId: string | null, paused: PausedTours): void {
  if (!userId) return;
  try {
    if (Object.keys(paused).length > 0) localStorage.setItem(storageKey(userId), JSON.stringify(paused));
    else localStorage.removeItem(storageKey(userId));
  } catch {
    // Storage blocked: the tour simply restarts from the beginning next time.
  }
}

function withoutTour(paused: PausedTours, tourId: string): PausedTours {
  if (!(tourId in paused)) return paused;
  const rest = { ...paused };
  delete rest[tourId];
  return rest;
}

interface TourStore {
  userId: string | null;
  activeTour: GuidedTour | null;
  stepIndex: number;
  paused: PausedTours;
  spotlightPosition: SpotlightPosition | null;

  /** Called when the logged-in user changes: drops the previous user's tour, loads this one's pause. */
  setUser: (userId: string | null) => void;

  /** Starts a tour, or resumes it where "Reprendre plus tard" left it. */
  start: (tourId: string) => void;
  next: () => void;
  previous: () => void;
  /** "Reprendre plus tard": closes the bubble and remembers the step. */
  pause: () => void;
  /** "Quitter": closes the bubble and forgets the step. */
  quit: () => void;
  finish: () => Promise<void>;
  setSpotlightPosition: (position: SpotlightPosition | null) => void;
}

export const useTourController = create<TourStore>((set, get) => ({
  userId: null,
  activeTour: null,
  stepIndex: 0,
  paused: NO_PAUSED_TOURS,
  spotlightPosition: null,

  setUser: (userId) => {
    if (userId === get().userId) return;
    set({
      userId,
      activeTour: null,
      stepIndex: 0,
      paused: userId ? readPausedTours(userId) : NO_PAUSED_TOURS,
      spotlightPosition: null,
    });
  },

  start: (tourId) => {
    const tour = findGuidedTour(tourId);
    if (!tour || tour.steps.length === 0) return;
    const pausedAt = get().paused[tourId];
    const resumeAt = pausedAt === undefined ? 0 : Math.min(Math.max(pausedAt, 0), tour.steps.length - 1);
    set({ activeTour: tour, stepIndex: resumeAt, spotlightPosition: null });
    trackTutorialStarted(tourId, 'tour');
  },

  next: () => {
    const { activeTour, stepIndex } = get();
    if (!activeTour || stepIndex >= activeTour.steps.length - 1) return;
    set({ stepIndex: stepIndex + 1 });
  },

  previous: () => {
    const { stepIndex } = get();
    if (stepIndex > 0) set({ stepIndex: stepIndex - 1 });
  },

  pause: () => {
    const { activeTour, stepIndex } = get();
    if (!activeTour) return;
    const paused = { ...get().paused, [activeTour.id]: stepIndex };
    writePausedTours(get().userId, paused);
    set({ activeTour: null, paused, spotlightPosition: null });
  },

  quit: () => {
    const { activeTour, userId } = get();
    const keepPaused = activeTour ? withoutTour(get().paused, activeTour.id) : get().paused;
    writePausedTours(userId, keepPaused);
    set({ activeTour: null, paused: keepPaused, stepIndex: 0, spotlightPosition: null });
  },

  finish: async () => {
    const { activeTour, userId } = get();
    if (!activeTour) return;
    const keepPaused = withoutTour(get().paused, activeTour.id);
    writePausedTours(userId, keepPaused);
    set({ activeTour: null, paused: keepPaused, stepIndex: 0, spotlightPosition: null });
    try {
      syncOnboardingCache(await onboardingService.completeStep(tutorialStepId(activeTour.id)));
      trackTutorialCompleted(activeTour.id, 'tour');
    } catch {
      // The tour stays "à faire": tell the user rather than failing silently.
      toast.error(i18n.t('tutorials:article.save_failed'));
    }
  },

  setSpotlightPosition: (position) => set({ spotlightPosition: position }),
}));
